import React, { useEffect, useMemo, useState } from "react";
import { api } from "../api";
import Header from "../components/Header";
import Nav from "../components/Nav";
import ServerPagination from "../components/ServerPagination";
import { sortItems, useSortState, SortTh, useColumnVisibility, ColumnMenu } from "../hooks/tableTools";

const PER_PAGE_OPTIONS = [50, 100, 200, 500, 1000];
type Tab = "schedule" | "reference";

interface Col {
  key: string;
  label: string;
  sortKey: string | null;
  render: (r: any) => React.ReactNode;
}

export default function GoldReference() {
  // ── Shared filter / data state ───────────────────────────────────────────
  const [rows, setRows] = useState<any[]>([]);
  const [markets, setMarkets] = useState<string[]>([]);
  const [schemas, setSchemas] = useState<string[]>([]);
  const [market, setMarket] = useState("");
  const [schema, setSchema] = useState("");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(200);
  const [total, setTotal] = useState(0);

  // ── Tab state ────────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState<Tab>("schedule");
  const [overridesOnly, setOverridesOnly] = useState(false);

  // ── Schedule-tab edit state ──────────────────────────────────────────────
  const [editKey, setEditKey] = useState<string | null>(null);
  const [editStart, setEditStart] = useState("");
  const [editEnd, setEditEnd] = useState("");
  const [resetting, setResetting] = useState<string | null>(null);

  // ── Status state ─────────────────────────────────────────────────────────
  const [flash, setFlash] = useState("");

  // ── Sort state — one per tab so switching resets sort independently ───────
  const scheduleSort = useSortState();
  const referenceSort = useSortState();
  const { sortKey, sortDir, onSort, clearSort } =
    activeTab === "schedule" ? scheduleSort : referenceSort;

  // ── Column visibility — one per tab ──────────────────────────────────────
  const scheduleKeys = ["job_name", "market", "schema_name", "day_of_week", "expected_start", "expected_end", "hist_avg_start", "hist_avg_end", "hist_valid_runs", "source_badge", "actions"];
  const referenceKeys = ["job_name", "market", "schema_name", "day_of_week", "fs61_start", "fs61_end", "our_sla_start", "our_sla_end", "comment", "last_hist_updated"];
  const schedVis = useColumnVisibility(scheduleKeys);
  const refVis   = useColumnVisibility(referenceKeys);
  const { hidden, toggle, selectAll, allChecked } =
    activeTab === "schedule" ? schedVis : refVis;

  // ── Data load ────────────────────────────────────────────────────────────
  async function load() {
    const params: Record<string, any> = { page, per_page: perPage };
    if (market) params.market = market;
    if (schema) params.schema = schema;
    if (q) params.q = q;
    if (overridesOnly) params.overrides_only = true;
    const { data } = await api.goldReference(params);
    if (data) {
      setRows(data.rows || []);
      setMarkets(data.markets || []);
      setSchemas(data.schemas || []);
      setTotal(data.total || 0);
    }
  }

  useEffect(() => { load(); }, [market, schema, q, page, perPage, overridesOnly]);

  // ── Helpers ──────────────────────────────────────────────────────────────
  function showFlash(msg: string) {
    setFlash(msg);
    setTimeout(() => setFlash(""), 2500);
  }

  function switchTab(tab: Tab) {
    if (tab === activeTab) return;
    setActiveTab(tab);
    setEditKey(null);
    setPage(1);
    if (tab !== "schedule") setOverridesOnly(false);
  }

  function clearAllFilters() {
    setMarket(""); setSchema(""); setQ(""); setOverridesOnly(false); setPage(1);
  }

  // ── Schedule-tab actions ─────────────────────────────────────────────────

  function startEdit(row: any) {
    setEditKey(`${row.schema_name}||${row.job_name}||${row.day_of_week}`);
    setEditStart(row.expected_start || "");
    setEditEnd(row.expected_end || "");
  }

  async function saveEdit(row: any) {
    const { ok, data } = await api.goldReferenceUpdate({
      schema_name: row.schema_name, job_name: row.job_name, day_of_week: row.day_of_week,
      expected_start: editStart, expected_end: editEnd,
    });
    if (ok) {
      setEditKey(null);
      showFlash("Saved — marked as override");
      load();
    } else {
      showFlash((data && data.error) || "Save failed");
    }
  }

  async function resetSchedule(row: any) {
    const key = `${row.schema_name}||${row.job_name}||${row.day_of_week}`;
    setResetting(key);
    try {
      const { ok, data } = await api.goldReferenceResetSchedule({
        schema_name: row.schema_name, job_name: row.job_name, market: row.market,
      });
      if (ok) { showFlash("Reset to auto (hist avg)"); load(); }
      else showFlash((data && data.error) || "Reset failed");
    } finally {
      setResetting(null);
    }
  }

  // ── Column definitions ───────────────────────────────────────────────────
  const SCHEDULE_COLS: Col[] = useMemo(() => [
    {
      key: "job_name", label: "Job", sortKey: "job_name",
      render: (r) => <span className="job-name" title={r.job_name}>{r.job_name}</span>,
    },
    {
      key: "market", label: "Market", sortKey: "market",
      render: (r) => r.market || "-",
    },
    {
      key: "schema_name", label: "Schema", sortKey: "schema_name",
      render: (r) => <span style={{ fontSize: ".75rem", color: "#64748b" }}>{r.schema_name}</span>,
    },
    {
      key: "day_of_week", label: "Day", sortKey: "day_of_week",
      render: (r) => <span style={{ fontWeight: 500 }}>{r.day_of_week}</span>,
    },
    {
      key: "expected_start", label: "Exp Start", sortKey: "expected_start",
      render: (r) => {
        const key = `${r.schema_name}||${r.job_name}||${r.day_of_week}`;
        if (editKey === key)
          return <input className="edit-input" value={editStart} onChange={(e) => setEditStart(e.target.value)} placeholder="HH:MM" />;
        return (
          <span className="fs61-time" title={r.is_overridden ? "User override" : "Auto (hist avg)"}>
            {r.expected_start || "-"}
            {r.expected_start && (
              r.is_overridden
                ? <span style={{ color: "#f59e0b", fontSize: ".65rem", marginLeft: 3 }} title="Override">●</span>
                : <span style={{ color: "#10b981", fontSize: ".65rem", marginLeft: 3 }} title="Auto">●</span>
            )}
          </span>
        );
      },
    },
    {
      key: "expected_end", label: "Exp End", sortKey: "expected_end",
      render: (r) => {
        const key = `${r.schema_name}||${r.job_name}||${r.day_of_week}`;
        return editKey === key
          ? <input className="edit-input" value={editEnd} onChange={(e) => setEditEnd(e.target.value)} placeholder="HH:MM" />
          : <span className="fs61-time">{r.expected_end || "-"}</span>;
      },
    },
    {
      key: "hist_avg_start", label: "Hist Avg Start", sortKey: "hist_avg_start",
      render: (r) => <span className="hist-time">{r.hist_avg_start || "-"}</span>,
    },
    {
      key: "hist_avg_end", label: "Hist Avg End", sortKey: "hist_avg_end",
      render: (r) => <span className="hist-time">{r.hist_avg_end || "-"}</span>,
    },
    {
      key: "hist_valid_runs", label: "Hist Runs", sortKey: "hist_valid_runs",
      render: (r) => <span style={{ color: "#888" }}>{r.hist_valid_runs ?? 0}</span>,
    },
    {
      key: "source_badge", label: "Source", sortKey: null,
      render: (r) => {
        if (r.is_overridden)
          return <span style={{ background: "#fef3c7", color: "#b45309", borderRadius: 4, padding: "1px 7px", fontSize: ".72rem", fontWeight: 600 }}>Override</span>;
        if (r.hist_avg_start || r.hist_avg_end)
          return <span style={{ background: "#dcfce7", color: "#166534", borderRadius: 4, padding: "1px 7px", fontSize: ".72rem", fontWeight: 600 }}>Auto</span>;
        return <span style={{ background: "#f1f5f9", color: "#64748b", borderRadius: 4, padding: "1px 7px", fontSize: ".72rem" }}>No data</span>;
      },
    },
    {
      key: "actions", label: "Actions", sortKey: null,
      render: (r) => {
        const key = `${r.schema_name}||${r.job_name}||${r.day_of_week}`;
        const isResetting = resetting === key;
        if (editKey === key)
          return (
            <>
              <button className="btn-save" onClick={() => saveEdit(r)}>Save</button>{" "}
              <button className="btn-cancel" onClick={() => setEditKey(null)}>Cancel</button>
            </>
          );
        return (
          <>
            <button className="btn-edit" onClick={() => startEdit(r)}>Edit</button>
            {r.is_overridden && (
              <button className="btn-cancel" style={{ marginLeft: 4 }} disabled={isResetting}
                onClick={() => resetSchedule(r)} title="Revert to ML-computed hist avg">
                {isResetting ? "…" : "Reset"}
              </button>
            )}
          </>
        );
      },
    },
  ], [editKey, editStart, editEnd, resetting]);

  const REFERENCE_COLS: Col[] = useMemo(() => [
    {
      key: "job_name", label: "Job", sortKey: "job_name",
      render: (r) => <span className="job-name" title={r.job_name}>{r.job_name}</span>,
    },
    {
      key: "market", label: "Market", sortKey: "market",
      render: (r) => r.market || "-",
    },
    {
      key: "schema_name", label: "Schema", sortKey: "schema_name",
      render: (r) => <span style={{ fontSize: ".75rem", color: "#64748b" }}>{r.schema_name}</span>,
    },
    {
      key: "day_of_week", label: "Day", sortKey: "day_of_week",
      render: (r) => <span style={{ fontWeight: 500 }}>{r.day_of_week}</span>,
    },
    {
      key: "fs61_start", label: "FS61 Start", sortKey: "fs61_start",
      render: (r) => <span className="fs61-time">{r.fs61_start || "-"}</span>,
    },
    {
      key: "fs61_end", label: "FS61 End", sortKey: "fs61_end",
      render: (r) => <span className="fs61-time">{r.fs61_end || "-"}</span>,
    },
    {
      key: "our_sla_start", label: "SLA Start", sortKey: "our_sla_start",
      render: (r) => <span style={{ color: "#555" }}>{r.our_sla_start || "-"}</span>,
    },
    {
      key: "our_sla_end", label: "SLA End", sortKey: "our_sla_end",
      render: (r) => <span style={{ color: "#555" }}>{r.our_sla_end || "-"}</span>,
    },
    {
      key: "comment", label: "Comment", sortKey: null,
      render: (r) => (
        <span style={{ color: "#666", fontSize: ".78rem", fontStyle: r.comment ? "normal" : "italic" }}>
          {r.comment || "—"}
        </span>
      ),
    },
    {
      key: "last_hist_updated", label: "Last Updated", sortKey: "last_hist_updated",
      render: (r) => <span style={{ fontSize: ".72rem", color: "#555" }}>{(r.last_hist_updated || "").slice(0, 10)}</span>,
    },
  ], []);

  // ── Derived view data ─────────────────────────────────────────────────────
  const activeCols = activeTab === "schedule" ? SCHEDULE_COLS : REFERENCE_COLS;
  const visibleColumns = activeCols.filter((c) => !hidden.has(c.key));
  const sortedRows = useMemo(() => sortItems(rows, sortKey, sortDir), [rows, sortKey, sortDir]);
  const totalPages = Math.max(1, Math.ceil(total / perPage));

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      <Header />
      <Nav />

      {/* Tab bar */}
      <div style={{ display: "flex", gap: 0, borderBottom: "2px solid #e2e8f0", padding: "0 16px", background: "#fff" }}>
        {(
          [
            ["schedule", "🤖 Schedule Rules (ML)"],
            ["reference", "📋 Reference (FS61 / SLA)"],
          ] as [Tab, string][]
        ).map(([tab, label]) => (
          <button
            key={tab}
            onClick={() => switchTab(tab)}
            style={{
              padding: "10px 20px",
              border: "none",
              borderBottom: activeTab === tab ? "3px solid #2563eb" : "3px solid transparent",
              background: "none",
              cursor: "pointer",
              fontWeight: activeTab === tab ? 700 : 400,
              color: activeTab === tab ? "#2563eb" : "#64748b",
              fontSize: ".88rem",
              transition: "color .15s, border-color .15s",
              marginBottom: -2,
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Reference tab: info banner */}
      {activeTab === "reference" && (
        <div className="refresh-banner" style={{ background: "#f8fafc" }}>
          <span style={{ color: "#555", fontSize: ".78rem" }}>
            FS61 and SLA reference data — read-only display. To edit these values, use{" "}
            <strong>Asset Tagging → Gold</strong>.
          </span>
          {flash && (
            <span style={{ marginLeft: 12, color: "#1e8e3e", fontWeight: 600, fontSize: ".8rem" }}>
              {flash}
            </span>
          )}
        </div>
      )}

      {/* Filter bar — shared across tabs */}
      <div className="filter-bar">
        <select value={market} onChange={(e) => { setMarket(e.target.value); setPage(1); }}>
          <option value="">All Markets</option>
          {markets.map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
        <select value={schema} onChange={(e) => { setSchema(e.target.value); setPage(1); }}>
          <option value="">All Schemas</option>
          {schemas.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <input
          type="text"
          placeholder="Search job name…"
          value={q}
          onChange={(e) => { setQ(e.target.value); setPage(1); }}
        />
        {activeTab === "schedule" && (
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: ".82rem", color: "#555", cursor: "pointer", userSelect: "none" }}>
            <input
              type="checkbox"
              checked={overridesOnly}
              onChange={(e) => { setOverridesOnly(e.target.checked); setPage(1); }}
            />
            Overrides only
          </label>
        )}
        <div className="spacer" />
        <span style={{ color: "#666", fontSize: ".78rem" }}>{total.toLocaleString()} rows</span>
        {flash && activeTab !== "schedule" && (
          <span className="saved-flash">{flash}</span>
        )}
        <div className="controls-actions">
          <button type="button" className="btn-sm" onClick={clearSort}>&#10005; Clear Sort</button>
          <button type="button" className="btn-sm" onClick={clearAllFilters}>&#10005; Clear Filters</button>
          <ColumnMenu
            columns={activeCols}
            hidden={hidden}
            toggle={toggle}
            selectAll={selectAll}
            allChecked={allChecked}
          />
        </div>
      </div>

      {/* Table */}
      <div className="ref-table-wrap">
        <table className="ref-table">
          <thead>
            <tr>
              {visibleColumns.map((c) => (
                <SortTh key={c.key} columnKey={c.sortKey} sortKey={sortKey} sortDir={sortDir} onSort={onSort}>
                  {c.label}
                </SortTh>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedRows.length === 0 ? (
              <tr>
                <td colSpan={visibleColumns.length} className="loading">No rows</td>
              </tr>
            ) : (
              sortedRows.map((r: any) => {
                const key = `${r.schema_name}||${r.job_name}||${r.day_of_week}`;
                return (
                  <tr
                    key={key}
                    style={
                      activeTab === "schedule" && r.is_overridden
                        ? { background: "rgba(245,158,11,.05)" }
                        : undefined
                    }
                  >
                    {visibleColumns.map((c) => <td key={c.key}>{c.render(r)}</td>)}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <ServerPagination
        page={page}
        totalPages={totalPages}
        total={total}
        perPage={perPage}
        perPageOptions={PER_PAGE_OPTIONS}
        onPerPageChange={(n) => { setPerPage(n); setPage(1); }}
        onPageChange={setPage}
      />
    </>
  );
}
